import { COLORS } from '@styles/colors'
import { Container, Section } from '@styles/global.styled'
import { useInView } from 'react-intersection-observer'
import useSalesComparison from '@hooks/useSalesComparison'
import PoweredBy from '../PoweredBy'
import SuburbProfileFilters from '../SuburbProfileFilters'
import FiveYearGrowth, { hasValidFiveYearGrowthItem } from './FiveYearGrowth'
import * as S from './SalesComparison.styled'
import React from 'react'

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-au', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value)
}

const SalesComparison = ({
  fiveYearGrowth: initialFiveYearGrowth,
  salesComparison: initialSalesComparison,
  suburb,
  slug,
  availableBedrooms,
}) => {
  const [type, setType] = React.useState('all')
  const [bedrooms, setBedrooms] = React.useState('Overall')
  const { salesComparison } = useSalesComparison(
    slug,
    type,
    bedrooms,
    initialSalesComparison
  )

  const [data, setData] = React.useState(initialSalesComparison?.house || [])
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  React.useEffect(() => {
    if (salesComparison) {
      setData(salesComparison?.items || [])
    }
  }, [salesComparison])

  const maxPrices = Math.max(
    ...(salesComparison?.items || []).map((it) => it.price)
  )

  const hasValidSalesComparisonItem = (data) => {
    return data.some(item =>
      item.price > 0
    );
  };

  const shouldRenderSalesComparison = hasValidFiveYearGrowthItem(initialFiveYearGrowth || [])
  const shouldRenderSalesComparisonSection = data.length > 0 && hasValidSalesComparisonItem(data)
  const shouldRenderSection = shouldRenderSalesComparisonSection || shouldRenderSalesComparison

  if (!shouldRenderSection) {
    return null
  }

  return (
    <Section
      withPaddingBottom
      style={{ backgroundColor: COLORS.nature[95] }}
      ref={ref}
    >
      <Container>
        <PoweredBy />
        {data.length > 0 && (
          <S.Group>
            <S.GroupHeading>
              <S.Title>SALES COMPARISON</S.Title>
              <SuburbProfileFilters
                bedrooms={bedrooms}
                availableBedrooms={availableBedrooms}
                onBedroomsChange={setBedrooms}
                type={type}
                onTypeChange={setType}
              />
            </S.GroupHeading>
            <S.Description>
              Find out how {suburb} median sales prices compare to neighbouring
              suburbs.
            </S.Description>

            {inView && (
              <S.ChartWrapper>
                {data.map((item, index) => {
                  const widthPercent = (item.price / maxPrices) * 100
                  return (
                    <S.BarRow key={item.suburb}>
                      <S.Bar
                        width={widthPercent}
                        highlight={item.is_current_suburb || item.is_current}
                        index={index}
                      >
                        <S.BarLabel>
                          {item.suburb} {formatCurrency(item.price)}
                        </S.BarLabel>
                      </S.Bar>
                    </S.BarRow>
                  )
                })}
              </S.ChartWrapper>
            )}
          </S.Group>
        )}

        <FiveYearGrowth
          fiveYearGrowth={initialFiveYearGrowth}
          suburb={suburb}
          slug={slug}
          availableBedrooms={availableBedrooms}
        />
      </Container>
    </Section>
  )
}

export default SalesComparison
